import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { VegaEmbed } from 'react-vega'
import type { VisualizationSpec } from 'vega-embed'
import type { Config } from 'vega-lite'

import { ConditionalFormatProvider } from '../components/ConditionalFormatContext'
import { DataTableCard } from '../components/DataTableCard'
import { FormattedTableCell } from '../components/FormattedTableCell'
import { PageScrollLayout } from '../components/PageScrollLayout'
import { nextSortDirection, SortableTableHeader, type SortDirection } from '../components/SortableTableHeader'
import { Api, ApiError, type CurrentRatingRow, type RatingsByDayRow, type TeamMappingRow } from '../services/api'
import {
  type DivisionFilter,
  type LeagueFilter,
  buildTeamRegionMap,
  teamPassesRegionFilters,
} from '../utils/mlbRegion'
import { teamColor, teamColorScale } from '../utils/mlbTeamColors'
import { RATINGS_COLUMN_SCALES, RATINGS_HEATMAP_COLUMNS } from '../utils/tableHeatmapScales'

type RatingMetric = 'Current' | 'PS Rating' | '2026 Rating' | 'Pyth' | 'Total' | 'SOS' | 'rSOS'
type TableSortKey = keyof Pick<
  CurrentRatingRow,
  | 'Team'
  | 'W-L'
  | 'Current'
  | 'Pyth'
  | 'PS Rating'
  | '2026 Rating'
  | 'PS Weight'
  | 'Total'
  | 'Total Rk'
  | 'SOS'
  | 'SOS Rk'
  | 'rSOS'
  | 'rSOS Rk'
>

const SORT_KEYS: { value: RatingMetric; label: string }[] = [
  { value: 'Current', label: 'Current' },
  { value: 'PS Rating', label: 'PS Rating' },
  { value: '2026 Rating', label: '2026 Rating' },
  { value: 'Pyth', label: 'Pyth Win %' },
  { value: 'Total', label: 'Total' },
  { value: 'SOS', label: 'SOS' },
  { value: 'rSOS', label: 'rSOS' },
]

const VEGA_CONFIG: Config = {
  axis: {
    labelColor: '#64748b',
    titleColor: '#64748b',
    gridColor: '#e2e8f0',
    domainColor: '#cbd5e1',
    labelFontSize: 11,
    titleFontSize: 12,
  },
  legend: { labelColor: '#334155', titleColor: '#334155', labelFontSize: 12 },
  view: { stroke: null },
}

const LEAGUE_OPTIONS: { value: LeagueFilter; label: string }[] = [
  { value: 'all', label: 'All leagues' },
  { value: 'AL', label: 'AL' },
  { value: 'NL', label: 'NL' },
]

const DIVISION_OPTIONS: { value: DivisionFilter; label: string }[] = [
  { value: 'all', label: 'All divisions' },
  { value: 'East', label: 'East' },
  { value: 'Central', label: 'Central' },
  { value: 'West', label: 'West' },
]

function toYmd(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') {
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/)
    return m ? m[1]! : v.slice(0, 10)
  }
  return ''
}

function metricValue(row: CurrentRatingRow, key: RatingMetric): number {
  const v = row[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : NaN
}

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

/** Format a YYYY-MM-DD as "Thu, May 7, 2026" (interpreted at local noon to avoid TZ drift). */
function formatYmd(ymd: string): string {
  if (!ymd) return ''
  const d = new Date(`${ymd}T12:00:00`)
  return Number.isNaN(d.getTime()) ? ymd : DATE_FORMATTER.format(d)
}

export function RatingsPage() {
  const ratingsQuery = useQuery({
    queryKey: ['ratings'],
    queryFn: Api.ratings,
  })

  const historyQuery = useQuery({
    queryKey: ['ratings_by_day'],
    queryFn: async () => {
      try {
        return await Api.getDataset<RatingsByDayRow>('ratings_by_day', {
          orderBy: 'snapshot_date, Team',
        })
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
  })

  const historyRows = historyQuery.data?.rows ?? []

  const teamMappingQuery = useQuery({
    queryKey: ['team_mapping'],
    queryFn: () => Api.getDataset<TeamMappingRow>('team_mapping', { orderBy: 'Mapping_Name' }),
  })

  const teamRegionMeta = useMemo(
    () => buildTeamRegionMap(teamMappingQuery.data?.rows ?? []),
    [teamMappingQuery.data],
  )

  const [leagueFilter, setLeagueFilter] = useState<LeagueFilter>('all')
  const [divisionFilter, setDivisionFilter] = useState<DivisionFilter>('all')

  const distinctDates = useMemo(() => {
    const s = new Set<string>()
    for (const r of historyRows) {
      const d = toYmd(r.snapshot_date)
      if (d) s.add(d)
    }
    return [...s].sort()
  }, [historyRows])

  const latestYmd = distinctDates.length ? distinctDates[distinctDates.length - 1]! : ''
  const [snapshotYmd, setSnapshotYmd] = useState<string>('')
  useEffect(() => {
    if (!snapshotYmd && latestYmd) setSnapshotYmd(latestYmd)
  }, [latestYmd, snapshotYmd])

  useEffect(() => {
    if (!snapshotYmd || !distinctDates.length) return
    if (!distinctDates.includes(snapshotYmd)) setSnapshotYmd(latestYmd)
  }, [distinctDates, latestYmd, snapshotYmd])

  const effectiveSnapshot = snapshotYmd || latestYmd

  const allTeamNamesUnfiltered = useMemo(() => {
    const s = new Set<string>()
    for (const r of historyRows) s.add(String(r.Team))
    if (s.size === 0 && ratingsQuery.data?.length) {
      for (const r of ratingsQuery.data) s.add(String(r.Team))
    }
    return [...s].sort()
  }, [historyRows, ratingsQuery.data])

  const teamsMatchingFilters = useMemo(
    () =>
      allTeamNamesUnfiltered.filter((t) =>
        teamPassesRegionFilters(t, teamRegionMeta, leagueFilter, divisionFilter),
      ),
    [allTeamNamesUnfiltered, teamRegionMeta, leagueFilter, divisionFilter],
  )

  const [chartTeams, setChartTeams] = useState<string[]>([])
  // Auto-select all teams that match the current league/division filter (and the
  // initial "all/all" state defaults to every team). Whenever the filter changes
  // or the team list arrives, replace the selection with the new full set.
  useEffect(() => {
    setChartTeams(teamsMatchingFilters)
  }, [teamsMatchingFilters])

  const [sortKey, setSortKey] = useState<TableSortKey>('Current')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [chartMetric, setChartMetric] = useState<RatingMetric>('Current')

  const tableRowsRaw: CurrentRatingRow[] = useMemo(() => {
    if (historyRows.length && effectiveSnapshot) {
      return historyRows.filter((r) => toYmd(r.snapshot_date) === effectiveSnapshot)
    }
    return ratingsQuery.data ?? []
  }, [historyRows, effectiveSnapshot, ratingsQuery.data])

  const tableRows: CurrentRatingRow[] = useMemo(
    () =>
      tableRowsRaw.filter((r) =>
        teamPassesRegionFilters(String(r.Team), teamRegionMeta, leagueFilter, divisionFilter),
      ),
    [tableRowsRaw, teamRegionMeta, leagueFilter, divisionFilter],
  )

  const sorted = useMemo(() => {
    if (!tableRows.length) return []
    return sortRatingsRows(tableRows, sortKey, sortDirection)
  }, [tableRows, sortDirection, sortKey])

  const chartVegaData = useMemo(() => {
    const rows: { date: string; team: string; value: number }[] = []
    for (const r of historyRows) {
      const d = toYmd(r.snapshot_date)
      if (!d) continue
      const team = String(r.Team)
      if (!chartTeams.includes(team)) continue
      if (!teamPassesRegionFilters(team, teamRegionMeta, leagueFilter, divisionFilter)) continue
      const val = metricValue(r, chartMetric)
      if (!Number.isFinite(val)) continue
      // Append T12:00:00 (local time) so Vega parses it as noon local — keeps
      // the calendar date the same in any timezone (avoids a UTC-midnight shift
      // that displayed YYYY-MM-DD as the day before in negative-UTC zones).
      rows.push({ date: `${d}T12:00:00`, team, value: val })
    }
    rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.team.localeCompare(b.team)))
    return rows
  }, [historyRows, chartTeams, chartMetric, teamRegionMeta, leagueFilter, divisionFilter])

  const ratingsTrendSpec = useMemo((): VisualizationSpec => {
    const yTitle =
      chartMetric === 'Pyth'
        ? 'Pyth (win prob.)'
        : SORT_KEYS.find((k) => k.value === chartMetric)?.label ?? chartMetric
    const valueFormat = chartMetric === 'Pyth' ? '.1%' : '.3f'
    const colorScale = teamColorScale(chartTeams)
    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      description: 'Team ratings over snapshot dates',
      data: { values: chartVegaData },
      config: VEGA_CONFIG,
      width: 'container',
      height: 'container',
      // Default autosize ("pad") grows the SVG outside the specified plot box
      // to fit axes/labels, which made the rendered chart taller than the
      // parent's `h-80` (and our team-checkbox row got squeezed). "fit" with
      // `contains: 'padding'` keeps the entire chart — axes included — inside
      // the container's box.
      autosize: { type: 'fit', contains: 'padding' },
      params: [
        {
          name: 'hover',
          select: {
            type: 'point',
            fields: ['team'],
            on: 'pointerover',
            clear: 'pointerout',
            nearest: true,
          },
        },
      ],
      mark: {
        type: 'line',
        interpolate: 'monotone',
        strokeWidth: 2,
        invalid: null,
        point: { filled: true, size: 35 },
      },
      encoding: {
        x: {
          field: 'date',
          type: 'temporal',
          title: null,
          sort: 'ascending',
          axis: { format: '%Y-%m-%d', labelAngle: -35, labelOverlap: 'parity' },
        },
        y: {
          field: 'value',
          type: 'quantitative',
          title: yTitle,
          scale: { zero: false },
        },
        color: {
          field: 'team',
          type: 'nominal',
          title: 'Team',
          scale: { domain: colorScale.domain, range: colorScale.range },
          legend: chartTeams.length > 12 ? null : { symbolStrokeWidth: 3 },
        },
        opacity: {
          condition: { param: 'hover', empty: true, value: 0.85 },
          value: 0.18,
        },
        strokeWidth: {
          condition: { param: 'hover', empty: false, value: 4 },
          value: 2,
        },
        tooltip: [
          { field: 'team', type: 'nominal', title: 'Team' },
          { field: 'date', type: 'temporal', title: 'Date', format: '%Y-%m-%d' },
          { field: 'value', type: 'quantitative', title: yTitle, format: valueFormat },
        ],
      },
    }
  }, [chartMetric, chartTeams, chartVegaData])

  const allTeamNames = teamsMatchingFilters

  const loading = ratingsQuery.isLoading || historyQuery.isLoading || teamMappingQuery.isLoading
  const ratingsError = ratingsQuery.isError ? ratingsQuery.error : null
  const mappingError = teamMappingQuery.isError ? teamMappingQuery.error : null
  const historyError =
    historyQuery.isError && !(historyQuery.error instanceof ApiError && historyQuery.error.status === 404)
      ? historyQuery.error
      : null

  function toggleChartTeam(team: string) {
    setChartTeams((prev) =>
      prev.includes(team) ? prev.filter((t) => t !== team) : [...prev, team].sort(),
    )
  }

  function onSort(nextKey: TableSortKey) {
    if (nextKey === sortKey) {
      setSortDirection(nextSortDirection)
      return
    }
    setSortKey(nextKey)
    setSortDirection(defaultTableSortDirection(nextKey))
  }

  const getHeatmapValue = useCallback(
    (row: CurrentRatingRow, key: string) => row[key as keyof CurrentRatingRow],
    [],
  )

  const headerFilters = (
    <div className="flex flex-wrap items-center gap-3 text-sm">
          {distinctDates.length > 0 && (
            <label className="flex items-center gap-2">
              <span className="text-slate-500 whitespace-nowrap">Date</span>
              <select
                className="rounded border border-slate-300 px-2 py-1"
                value={effectiveSnapshot || ''}
                onChange={(e) => setSnapshotYmd(e.target.value)}
              >
                {/* Reverse so most recent is at the top. */}
                {[...distinctDates].reverse().map((d) => (
                  <option key={d} value={d}>
                    {formatYmd(d)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2">
            <span className="text-slate-500 whitespace-nowrap">League</span>
            <select
              className="rounded border border-slate-300 px-2 py-1"
              value={leagueFilter}
              onChange={(e) => setLeagueFilter(e.target.value as LeagueFilter)}
            >
              {LEAGUE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-slate-500 whitespace-nowrap">Division</span>
            <select
              className="rounded border border-slate-300 px-2 py-1"
              value={divisionFilter}
              onChange={(e) => setDivisionFilter(e.target.value as DivisionFilter)}
            >
              {DIVISION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
    </div>
  )

  return (
    <PageScrollLayout
      title="Ratings"
      subtitle="Strength estimates fed into the season simulator. Filter by league, division, and date; chart selected teams over time."
      headerExtra={headerFilters}
    >
      {historyRows.length > 0 && (
        <div className="card space-y-3 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Ratings over time</h2>
              <p className="text-xs text-slate-500">
                {distinctDates.length} day{distinctDates.length === 1 ? '' : 's'} available
                {distinctDates.length > 0 ? (
                  <>
                    {' '}({formatYmd(distinctDates[0]!)} → {formatYmd(distinctDates[distinctDates.length - 1]!)})
                  </>
                ) : null}
                . Hover a line to highlight that team.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Chart metric</span>
              <select
                className="rounded border border-slate-300 px-2 py-1"
                value={chartMetric}
              onChange={(e) => setChartMetric(e.target.value as RatingMetric)}
              >
                {SORT_KEYS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="h-80 w-full min-w-0">
            <VegaEmbed
              spec={ratingsTrendSpec}
              options={{ actions: false }}
              className="h-full w-full [&_svg]:max-w-full"
            />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
              <span>Teams shown on chart</span>
              <span className="flex gap-2">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-0.5 hover:bg-slate-100"
                  onClick={() => setChartTeams(allTeamNames)}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-0.5 hover:bg-slate-100"
                  onClick={() => setChartTeams([])}
                >
                  Clear
                </button>
              </span>
            </div>
            <div className="max-h-36 overflow-y-auto rounded border border-slate-200 bg-slate-50/80 px-3 py-2">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {allTeamNames.map((t) => (
                  <label key={t} className="flex cursor-pointer items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={chartTeams.includes(t)}
                      onChange={() => toggleChartTeam(t)}
                    />
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-slate-300"
                      style={{ backgroundColor: teamColor(t) }}
                    />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {historyRows.length === 0 && !historyQuery.isLoading && (
        <p className="text-sm text-amber-800">
          No <code className="text-xs">ratings_by_day</code> history yet — run the pipeline after deploying
          the ratings history step, or backfill with{' '}
          <code className="text-xs">python mlb-futures/backfill_ratings_by_day.py</code>. Showing live
          ratings only below.
        </p>
      )}

      {loading && <p className="text-slate-500">Loading…</p>}
      {(ratingsError || historyError || mappingError) && (
        <p className="text-rose-600">
          Failed to load: {String(ratingsError || historyError || mappingError)}
        </p>
      )}

      {sorted.length > 0 && (
        <div className="table-viewport-region flex h-[min(36rem,calc(100dvh-12rem))] min-h-0 flex-col">
          <ConditionalFormatProvider
            rows={sorted}
            columnKeys={RATINGS_HEATMAP_COLUMNS}
            columnScales={RATINGS_COLUMN_SCALES}
            getValue={getHeatmapValue}
          >
            <DataTableCard
              fillViewport
            caption={
              effectiveSnapshot && historyRows.length > 0 ? (
                <>Ratings as of {formatYmd(effectiveSnapshot)} (US/Eastern date).</>
              ) : (
                <>Current ratings from latest pipeline run.</>
              )
            }
          >
            <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <SortableTableHeader label="Team" sortKey="Team" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="W-L" sortKey="W-L" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="Current" sortKey="Current" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="Pyth" sortKey="Pyth" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="2026 Rating" sortKey="2026 Rating" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="PS Rating" sortKey="PS Rating" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="PS Weight" sortKey="PS Weight" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="Total" sortKey="Total" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="Total Rk" sortKey="Total Rk" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="SOS" sortKey="SOS" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="SOS Rk" sortKey="SOS Rk" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="rSOS" sortKey="rSOS" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                <SortableTableHeader label="rSOS Rk" sortKey="rSOS Rk" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={String(row.Team) + i}>
                  <td className="font-mono text-slate-500">{i + 1}</td>
                  <td className="font-medium">{String(row.Team ?? '—')}</td>
                  <td>{String(row['W-L'] ?? '—')}</td>
                  <FormattedTableCell columnKey="Current" value={row.Current} className="font-mono">
                    {fmt3(row.Current)}
                  </FormattedTableCell>
                  <FormattedTableCell columnKey="Pyth" value={row.Pyth} className="font-mono">
                    {fmtPct(row.Pyth)}
                  </FormattedTableCell>
                  <FormattedTableCell columnKey="2026 Rating" value={row['2026 Rating']} className="font-mono">
                    {fmt3(row['2026 Rating'])}
                  </FormattedTableCell>
                  <FormattedTableCell columnKey="PS Rating" value={row['PS Rating']} className="font-mono">
                    {fmt3(row['PS Rating'])}
                  </FormattedTableCell>
                  <FormattedTableCell columnKey="PS Weight" value={row['PS Weight']} className="font-mono">
                    {fmtPct(row['PS Weight'])}
                  </FormattedTableCell>
                  <FormattedTableCell columnKey="Total" value={row.Total} className="font-mono">
                    {fmt3(row.Total)}
                  </FormattedTableCell>
                  <td className="font-mono">{fmtRk(row['Total Rk'])}</td>
                  <FormattedTableCell columnKey="SOS" value={row.SOS} className="font-mono">
                    {fmt3(row.SOS)}
                  </FormattedTableCell>
                  <td className="font-mono">{fmtRk(row['SOS Rk'])}</td>
                  <FormattedTableCell columnKey="rSOS" value={row.rSOS} className="font-mono">
                    {fmt3(row.rSOS)}
                  </FormattedTableCell>
                  <td className="font-mono">{fmtRk(row['rSOS Rk'])}</td>
                </tr>
              ))}
            </tbody>
            </table>
            </DataTableCard>
          </ConditionalFormatProvider>
        </div>
      )}
    </PageScrollLayout>
  )
}

function sortRatingsRows(rows: CurrentRatingRow[], key: TableSortKey, direction: SortDirection): CurrentRatingRow[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const primary = compareSortValue(sortValue(a, key), sortValue(b, key))
    if (primary !== 0) return primary * multiplier
    return String(a.Team).localeCompare(String(b.Team))
  })
}

function defaultTableSortDirection(key: TableSortKey): SortDirection {
  return key === 'Team' ? 'asc' : 'desc'
}

function sortValue(row: CurrentRatingRow, key: TableSortKey): number | string {
  if (key === 'W-L') {
    const [wins, losses] = String(row['W-L'] ?? '').split('-').map((v) => Number(v))
    return Number.isFinite(wins) && Number.isFinite(losses) ? wins + (1 - losses / 1000) : String(row['W-L'] ?? '')
  }
  const value = row[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  return String(value ?? '')
}

function compareSortValue(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b))
}

function fmt3(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(3) : '—'
}

function fmtPct(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : '—'
}

function fmtRk(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(Math.round(v)) : '—'
}
